import { APIContext } from "../../types";
import { makePath } from "../../../url";
import * as APITypesV2 from "../../generated-types/v2/Api";
import { getJSON, postJSON, deleteJSON } from "../../request";
import { applyParams } from "../../../params";
import { applyPagination } from "../../pagination";

export type BookingAPI = ReturnType<typeof createBookingAPIv2>;

type SlotQueryParams = Omit<
  APITypesV2.BookingV2QueryBookableSlotsParams,
  "startTimeRange.start" | "startTimeRange.end"
> & {
  startTime?: DateRange;
};

type DateRange = {
  start?: string;
  end?: string;
};

type SiteScheduleQueryParams = Omit<
  APITypesV2.SlotV2GetSiteScheduleViewParams,
  "siteId"
>;

export function createBookingAPIv2({
  buildURL,
  fetchJSON,
  fetchRaw,
  fetchPagedJSON,
}: APIContext) {
  function getScheduleViewForSite(
    { siteId }: { siteId: string },
    options: SiteScheduleQueryParams
  ) {
    const path = makePath([
      "api",
      "v2",
      "booking",
      "site",
      siteId,
      "schedule-view",
    ]);
    const url = buildURL(path);
    const { end, start, ...rest } = options;
    const { searchParams } = url;

    if (start != null) {
      searchParams.append("start", start);
    }
    if (end != null) {
      searchParams.append("end", end);
    }

    applyPagination(searchParams, { ...rest });

    const request = getJSON(url);

    return fetchPagedJSON<APITypesV2.RoomWithSlotsV2>(request);
  }

  function querySlots({
    siteIds,
    startTime,
    includeClosedForPublicSlots,
    ...rest
  }: SlotQueryParams) {
    const path = makePath(["api", "v2", "booking", "slot"]);
    const url = buildURL(path);

    const params = url.searchParams;

    if (siteIds) {
      for (const siteId of siteIds) {
        params.append("siteIds", siteId);
      }
    }

    if (startTime?.start) {
      params.append("startTimeRange.start", startTime.start);
    }

    if (startTime?.end) {
      params.append("startTimeRange.end", startTime.end);
    }

    if (includeClosedForPublicSlots) {
      params.append(
        "includeClosedForPublicSlots",
        includeClosedForPublicSlots.toString()
      );
    }

    applyPagination(params, { ...rest });

    const request = getJSON(url);

    return fetchPagedJSON<APITypesV2.BookableSlot>(request);
  }

  function getSlot({ slotId }: { slotId: string }) {
    const path = makePath(["api", "v2", "booking", "slot", slotId]);
    const url = buildURL(path);
    const request = getJSON(url);

    return fetchJSON<APITypesV2.SlotV2>(request);
  }

  function bookSlot({
    slot,
    payload,
  }: {
    slot: Pick<APITypesV2.SlotV2, "slotId" | "eTag">;
    payload: APITypesV2.BookSlotRequest;
  }) {
    const path = makePath([
      "api",
      "v2",
      "booking",
      "slot",
      slot.slotId,
      "book",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, payload);
    request.headers.set("If-Match", slot.eTag);

    return fetchJSON<APITypesV2.SlotV2>(request);
  }

  function createSlot(payload: APITypesV2.CreateSlotV2Request) {
    const path = makePath(["api", "v2", "booking", "slot"]);
    const url = buildURL(path);
    const request = postJSON(url, payload);

    return fetchJSON<APITypesV2.SlotV2>(request);
  }

  function deleteSlot({
    eTag,
    slotId,
  }: Pick<APITypesV2.SlotV2, "slotId" | "eTag">) {
    const path = makePath(["api", "v2", "booking", "slot", slotId]);
    const url = buildURL(path);
    const request = deleteJSON(url, undefined);
    request.headers.set("If-Match", eTag);

    return fetchRaw(request);
  }

  function openSlot({ slotId }: { slotId: string }) {
    const path = makePath(["api", "v2", "booking", "slot", slotId, "open"]);
    const url = buildURL(path);
    const request = postJSON(url, undefined);

    return fetchJSON<APITypesV2.SlotV2>(request);
  }

  function closeSlot({ slotId }: { slotId: string }) {
    const path = makePath(["api", "v2", "booking", "slot", slotId, "close"]);
    const url = buildURL(path);
    const request = postJSON(url, undefined);

    return fetchJSON<APITypesV2.SlotV2>(request);
  }

  function unBookSlot({
    payload,
    slot,
  }: {
    slot: Pick<APITypesV2.SlotV2, "slotId" | "eTag">;
    payload: APITypesV2.UnBookSlotRequest;
  }) {
    const path = makePath([
      "api",
      "v2",
      "booking",
      "slot",
      slot.slotId,
      "unbook",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, payload);
    request.headers.set("If-Match", slot.eTag);

    return fetchJSON<APITypesV2.SlotV2>(request);
  }

  function getBookingTokensForPatient({
    patientId,
    ...params
  }: APITypesV2.BookingTokenQueryPatientsParams) {
    const path = makePath([
      "api",
      "v2",
      "patient",
      patientId,
      "booking",
      "token",
    ]);
    const url = buildURL(path);
    const { pageSize, continuationToken, ...rest } = params;
    applyParams(url.searchParams, {
      ...rest,
    });
    applyPagination(url.searchParams, {
      continuationToken: continuationToken,
      pageSize: pageSize,
    });

    const request = getJSON(url);

    return fetchPagedJSON<APITypesV2.BookingTokenV2>(request);
  }

  function createBookingTokenForPatient(patientId: string) {
    const path = makePath([
      "api",
      "v2",
      "patient",
      patientId,
      "booking",
      "token",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, undefined);
    return fetchJSON<APITypesV2.BookingTokenV2>(request);
  }

  return {
    openSlot,
    closeSlot,
    createSlot,
    deleteSlot,
    bookSlot,
    unBookSlot,
    getSlot,
    getScheduleViewForSite,
    querySlots,
    getBookingTokensForPatient,
    createBookingTokenForPatient,
  };
}
